import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sessionId =
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const SUGGESTIONS = [
  "How does Smile Rewards work?",
  "What services do you offer?",
  "How do I book an appointment?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the Smile Assistant 🦷 — ask me about our services, Smile Rewards, or booking a visit." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + payload.delta,
              };
              return copy;
            });
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-btn"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full btn-terra flex items-center justify-center shadow-lg"
        aria-label="Open chat"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-6 z-[60] w-[min(92vw,380px)] h-[min(70vh,560px)] bg-[#F9F6F0] border border-[#2A2E26]/10 rounded-[28px] shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-[#2A2E26] text-[#F9F6F0] px-5 py-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#C05C42] flex items-center justify-center">
                <Sparkles size={18} />
              </span>
              <div>
                <p className="font-display text-lg leading-none">Smile Assistant</p>
                <p className="text-xs text-[#A8B5A2] mt-1">Palmisano Family Dentistry</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#C05C42] text-[#F9F6F0] rounded-br-sm"
                        : "bg-[#F0EBE1] text-[#2A2E26] rounded-bl-sm"
                    }`}
                  >
                    {m.content || <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span>}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="pt-2 space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                      className="block w-full text-left text-sm px-4 py-2.5 rounded-xl border border-[#2A2E26]/15 text-[#4A4E46] hover:border-[#C05C42] hover:text-[#C05C42] transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-[#2A2E26]/10 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about services or rewards…"
                data-testid="chat-input"
                className="flex-1 rounded-full border border-[#2A2E26]/15 bg-white px-4 py-2.5 text-sm text-[#2A2E26] outline-none focus:border-[#C05C42] transition-colors"
              />
              <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send-btn"
                className="btn-terra w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-50 shrink-0">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Dot({ d = 0 }) {
  return (
    <motion.span
      className="w-1.5 h-1.5 rounded-full bg-[#C05C42] inline-block"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1, repeat: Infinity, delay: d }}
    />
  );
}
